/* eslint-disable max-len */
/* eslint-disable react/jsx-no-useless-fragment */
import {
  useState, useMemo, useEffect, useCallback, ChangeEvent,
} from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ExportXlsx from '../../../../../utils/ExportXlsx';

import DashboardService from '../../../../../services/affreightment/DashboardService';
import { Card, Input, LinhasPerformanceCardStyled } from './styles';

import NoData from '../../../../../components/NoData';
import FilterRadioButton from '../../../../../components/FilterRadioButtons';

import download from '../../../../../assets/images/icons/download.svg';
import maps from '../../../../../assets/images/icons/maps.svg';
import boarding from '../../../../../assets/images/icons/boarding.svg';
import useLocalState from '../../../../../hooks/useLocalState';
import { useAppContext } from '../../../../../contexts/auth';
import useApiCall from '../../../../../hooks/useApiCall';
import { AffreightmentRelResponse, LinesPerformanceType } from '../../types';

interface LinesPerformanceCardInterface {
  dashboardLinesPerformanceData: LinesPerformanceType;
  onFilteringByDate: ({ locTrab, date }: { locTrab: number, date: string }) => Promise<void>;
  selectedWorkplace: { value: number, label: string };
}

export default function LinesPerformanceCard({
  dashboardLinesPerformanceData,
  onFilteringByDate,
  selectedWorkplace,
}: LinesPerformanceCardInterface) {
  const currentDate = new Date().toJSON().slice(0, 10);

  const [isLinesPerformanceFilterLoading, setIsLinesPerformanceFilterLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBalancePointStatus, setSelectedBalancePointStatus] = useState('');
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [currentDateBeingShow, setCurrentDateBeingShow] = useState(currentDate);

  const { userData, selectedCompany } = useAppContext();
  const { userCode, userProfile } = userData;
  const companyCode = selectedCompany.value;

  const [, setLineMapFromDashboardBeingShow] = useLocalState('lineMapFromDashboardBeingShow');

  const { apiCall } = useApiCall();

  const formatedCurrentDateBeingShow = useMemo(() => {
    const day = currentDateBeingShow.split('-')[2];
    const month = currentDateBeingShow.split('-')[1];
    const year = currentDateBeingShow.split('-')[0];
    return `${day}/${month}/${year}`;
  }, [currentDateBeingShow]);

  const filteredLinhas = useMemo(() => dashboardLinesPerformanceData?.filter((linha) => (
    (
      linha.letreiro.toLowerCase().includes(searchTerm.toLowerCase())
    || linha.routeID.split('+')[1].toLowerCase().includes(searchTerm.toLowerCase())
    )
    && (
      String(linha.corPercOcupacao).includes(selectedBalancePointStatus)
    )
  )), [dashboardLinesPerformanceData, searchTerm, selectedBalancePointStatus]);

  const downloadUsersRel = useCallback(async (codViagem: string, codLinha: string) => {
    await toast.promise(apiCall({
      apiToCall: DashboardService.getRelsData,
      queryParams: { codUsu: userCode, perfilUsu: userProfile, codEmpresa: companyCode },
      reqBody: JSON.stringify({
        codViagem,
        codRelatorio: 1,
        dataViagens: currentDate,
      }),
      actionAfterResponse: (response: AffreightmentRelResponse) => {
        if (response.usuEmbarcados) {
          const usersRelData = response.usuEmbarcados.map((data) => {
            const dayEmbarque = data.dataEmb.split('T')[0].split('-')[2];
            const monthEmbarque = data.dataEmb.split('T')[0].split('-')[1];
            const yearEmbarque = data.dataEmb.split('T')[0].split('-')[0];
            return (
              {
                Linha: data.linha,
                'Usuario Embarcado': data.nome,
                'Sequência de embarque': data.seq,
                'Local de embarque': data.localEmb,
                'Dia do embarque': `${dayEmbarque}/${monthEmbarque}/${yearEmbarque}`,
                'Hora do embarque': data.dataEmb.split('T')[1].split('.')[0],
                'Titular | Avulso': data.statusUsu,
                'Linha titular': data.linhaOriginal,
                'Canal de registro': data.registro,

                'Valor VT/dia': data.valorVTDia.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
              }
            );
          });
          ExportXlsx({
            data: usersRelData,
            filename: `Embarcados na linha ${codLinha} em ${formatedCurrentDateBeingShow.replaceAll('/', '-')} emp${companyCode}`,
          });
        }

        if (!response.usuEmbarcados) {
          toast.error('Não há dados para serem baixados');
        }
      },
      catchMessage: 'Não foi possível baixar o relatório'
    }), {
      pending: 'Sua planilha está sendo baixada',
      success: 'Sua planilha foi baixada!',
      error: 'Não foi possível baixar sua planilha',
    });
  }, [apiCall, userCode, userProfile, companyCode, currentDate, formatedCurrentDateBeingShow]);

  const downloadTripsRel = useCallback(async () => {
    await toast.promise(apiCall({
      apiToCall: DashboardService.getRelsData,
      queryParams: { codUsu: userCode, perfilUsu: userProfile, codEmpresa: companyCode },
      reqBody: JSON.stringify({
        codViagem: 0,
        codRelatorio: 2,
        dataViagens: currentDateBeingShow,
      }),
      actionAfterResponse: (response: AffreightmentRelResponse) => {
        if (response.resumoLinha) {
          const tripsRelData = response.resumoLinha.map((data) => ({
            Linha: data.linha,
            Capacidade: data.capacidade,
            Ocupação: data.ocupacao,
            Titulares: data.titulares,
            Avulsos: data.avulsos,
            'Usuários de VT': `${data.percUsuVT}%`,

            'Custo da linha': data.valLInha.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),

            'Custo de VT': data.custoVT.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),

            'Custo Fret+VT': data.custoVtFreta.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),

            'Custo por usuário': data.custoUso.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
          }));
          ExportXlsx({
            data: tripsRelData,
            filename: `Viagens do dia ${formatedCurrentDateBeingShow.replaceAll('/', '-')} emp${companyCode}`,
          });
        }

        if (!response.resumoLinha) {
          toast.error('Não há dados para serem baixados');
        }
      },
      catchMessage: 'Não foi possível baixar o relatório',
    }), {
      pending: 'Sua planilha está sendo baixada',
      success: 'Sua planilha foi baixada!',
      error: 'Não foi possível baixar sua planilha',
    });
  }, [apiCall, userCode, userProfile, companyCode, currentDateBeingShow, formatedCurrentDateBeingShow]);

  function handleChangeSearchTerm(event: ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value);
  }

  function handleDateChange(event: ChangeEvent<HTMLInputElement>) {
    setSelectedDate(event.target.value);
  }

  useEffect(() => {
    setSelectedDate(currentDate);
    setCurrentDateBeingShow(currentDate);
  }, [currentDate]);

  return (
    <>
      {(!isLinesPerformanceFilterLoading) && (
        <Card justifyContent={dashboardLinesPerformanceData ? 'space-between' : 'center'}>
          <div className="card-title-linhasP">
            <div>Linhas - Performance</div>
          </div>

          <div className="card-filter-linhasP">
            <div className="data-filter">
            Data:
              <input type="date" value={selectedDate} onChange={handleDateChange} />
              <button
                type="button"
                onClick={async () => {
                  setIsLinesPerformanceFilterLoading(true);
                  await onFilteringByDate({
                    locTrab: selectedWorkplace.value,
                    date: selectedDate,
                  });
                  setIsLinesPerformanceFilterLoading(false);
                  setCurrentDateBeingShow(selectedDate);
                }}
              >
              Aplicar filtro
              </button>
            </div>

            {dashboardLinesPerformanceData && (
              <>
                <div>
                  <Input placeholder="Pesquisar Linha" onChange={handleChangeSearchTerm} />
                </div>
                <div className="radio-buttons">
                  <FilterRadioButton
                    onClick={() => setSelectedBalancePointStatus('')}
                    selected={selectedBalancePointStatus === ''}
                  >
                Todos
                  </FilterRadioButton>
                  <FilterRadioButton
                    onClick={() => setSelectedBalancePointStatus('1')}
                    selected={selectedBalancePointStatus === '1'}
                  >
                Acima PE
                  </FilterRadioButton>
                  <FilterRadioButton
                    onClick={() => setSelectedBalancePointStatus('3')}
                    selected={selectedBalancePointStatus === '3'}
                  >
                Abaixo PE
                  </FilterRadioButton>
                </div>
                <div className="download">
                  <img
                    src={download}
                    alt="download"
                    title={`Relatório de todas as viagens do dia ${formatedCurrentDateBeingShow}`}
                    onClick={() => downloadTripsRel()}
                  />
                </div>
              </>
            )}
          </div>

          {dashboardLinesPerformanceData && (
            <div className="card-main">
              {filteredLinhas.map((linha) => (
                <LinhasPerformanceCardStyled key={linha.routeID}>
                  <div className="info-linhas">
                    {`${linha.routeID.split('+')[1]} (${linha.sentido})`}
                    <label>
                      {linha.letreiro.replace('.kml', '')}
                    </label>
                  </div>

                  <div className="info-linhas">
                    {linha.capacidade}
                    <label>
                    Lugares
                    </label>
                  </div>

                  <div className="info-linhas">
                    {linha.ocupacao}
                    <label>
                    Ocupação D1
                    </label>
                  </div>

                  <div className="info-linhas">
                    {linha.titulares}
                    <label>
                      {linha.titulares == 1 ? 'Titular' : 'Titulares'}
                    </label>
                  </div>

                  <div className="info-linhas">
                    {linha.avulsos}
                    <label>
                      {linha.avulsos == 1 ? 'Avulso' : 'Avulsos'}
                    </label>
                  </div>

                  <div className="actions">
                    <Link
                      to="/dashboard/linhaMapa"
                      onClick={() => {
                        setLineMapFromDashboardBeingShow({
                          codLinha: linha.routeID.split('+')[1],
                          letreiro: linha.letreiro,
                          routeID: linha.routeID,
                          date: currentDateBeingShow,
                        });
                      }}
                    >
                      <img src={maps} alt="Maps" title="Mapa da linha e usuários" />
                    </Link>

                    <img
                      src={boarding}
                      alt="User"
                      title={`Relatório de embarcados da linha ${linha.routeID.split('+')[1]} no dia ${formatedCurrentDateBeingShow}`}
                      onClick={() => downloadUsersRel(String(linha.codviagem), linha.routeID.split('+')[1])}
                    />
                    {linha.corPercOcupacao == 1 && (
                      <div className="pe-green" title="Ponto de equilíbrio">
                        {`${linha.percPontoEq} %`}
                      </div>
                    )}
                    {linha.corPercOcupacao == 2 && (
                      <div className="pe-orange" title="Ponto de equilíbrio">
                        {`${linha.percPontoEq} %`}
                      </div>
                    )}
                    {linha.corPercOcupacao == 3 && (
                      <div className="pe-red" title="Ponto de equilíbrio">
                        {`${linha.percPontoEq} %`}
                      </div>
                    )}
                  </div>
                </LinhasPerformanceCardStyled>
              ))}
            </div>
          )}

          {(!dashboardLinesPerformanceData) && (
            <NoData
              icon="emptyBox"
              label={(
                <>
                Parece que não houve nenhuma viagem em
                  {' '}
                  <strong>{formatedCurrentDateBeingShow}</strong>
                  {' '}
                No local de trabaho
                  {' '}
                  <strong>
                    {selectedWorkplace.label}
                  .
                  </strong>
                  <br />
                Tente alterar a data no campo acima, ou o local de trabalho selecionado
                e clicar no botão
                  <br />
                  <strong>Aplicar Filtro</strong>
                </>
              )}
            />
          )}

          {(dashboardLinesPerformanceData && filteredLinhas.length === 0 && selectedBalancePointStatus === '1')
        && (
          <NoData
            icon="searchNotFound"
            label={(
              <>
                Não houve nenhuma viagem com status
                {' '}
                <strong>acima do ponto de equilíbrio</strong>
                {' '}
                no local de trabalho
                {' '}
                <strong>{selectedWorkplace.label}</strong>
                {' '}
                em
                {' '}
                <strong>{formatedCurrentDateBeingShow}</strong>
              </>
            )}
          />
        )}

          {(dashboardLinesPerformanceData && filteredLinhas.length === 0 && selectedBalancePointStatus === '3')
        && (
          <NoData
            icon="searchNotFound"
            label={(
              <>
                Não houve nenhuma viagem com status
                {' '}
                <strong>abaixo do ponto de equilíbrio</strong>
                {' '}
                no local de trabalho
                {' '}
                <strong>{selectedWorkplace.label}</strong>
                {' '}
                em
                {' '}
                <strong>{formatedCurrentDateBeingShow}</strong>
              </>
            )}
          />
        )}

          {(dashboardLinesPerformanceData && filteredLinhas.length === 0 && selectedBalancePointStatus === '')
        && (
          <NoData
            icon="searchNotFound"
            label={(
              <>
                Nenhum resultado foi encontrado para
                {' '}
                <strong>{searchTerm}</strong>
              </>
            )}
          />
        )}
        </Card>
      )}
    </>
  );
}
